import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Send, Mail } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../ui/dialog";
import { Button } from "../../ui/button";
import { Input } from "../../ui/input";
import { API } from "../../../App";
import { formatDateSafe } from "../../../lib/formatDate";

/** Envoi de la guidance parentale aux parents (SMTP du thérapeute, marque du Cabinet, PDF joint). */
export const ParentEmailDialog = ({ open, onOpenChange, report, onSent }) => {
  const [to, setTo] = useState("");
  const [subject, setSubject] = useState("");
  const [intro, setIntro] = useState("");
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState("");

  useEffect(() => {
    if (!open) return;
    setPreview(null); setIntro(""); setSubject("");
    axios.post(`${API}/session-reports/${report.report_id}/analyses/parent_guidance/email/preview`, {}, { withCredentials: true })
      .then((r) => { setPreview(r.data); setTo((r.data.to || []).join(", ")); setSubject(r.data.subject || ""); })
      .catch((e) => toast.error(e.response?.data?.detail || "Impossible de préparer l'e-mail"));
  }, [open, report.report_id]);

  const refresh = async () => {
    setBusy("preview");
    try {
      const r = await axios.post(`${API}/session-reports/${report.report_id}/analyses/parent_guidance/email/preview`, { to, subject, intro }, { withCredentials: true });
      setPreview(r.data);
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
    finally { setBusy(""); }
  };

  const send = async () => {
    if (!to.trim()) { toast.error("Indiquez au moins une adresse e-mail de parent"); return; }
    setBusy("send");
    try {
      const r = await axios.post(`${API}/session-reports/${report.report_id}/analyses/parent_guidance/email`, { to, subject, intro }, { withCredentials: true });
      toast.success(`Guidance envoyée à ${r.data.sent_to.join(", ")}`);
      onSent?.(r.data); onOpenChange(false);
    } catch (e) { toast.error(e.response?.data?.detail || "Échec de l'envoi"); }
    finally { setBusy(""); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="parent-email-dialog">
        <DialogHeader><DialogTitle className="font-heading flex items-center gap-2"><Mail className="w-5 h-5" /> Envoyer la guidance aux parents</DialogTitle></DialogHeader>
        <div className="space-y-2 text-sm">
          <label className="block">
            <span className="text-xs text-muted-foreground">Destinataires (parents) — séparés par des virgules</span>
            <Input value={to} onChange={(e) => setTo(e.target.value)} placeholder="parent1@exemple.fr, parent2@exemple.fr" data-testid="parent-email-to" />
          </label>
          <label className="block">
            <span className="text-xs text-muted-foreground">Objet</span>
            <Input value={subject} onChange={(e) => setSubject(e.target.value)} data-testid="parent-email-subject" />
          </label>
          <label className="block">
            <span className="text-xs text-muted-foreground">Mot d'introduction personnalisé (optionnel, remplace le texte par défaut)</span>
            <textarea value={intro} onChange={(e) => setIntro(e.target.value)} onBlur={refresh} rows={2} className="w-full rounded-md border bg-background px-3 py-2 text-sm" placeholder="Bonjour Madame, Monsieur, suite à notre séance…" data-testid="parent-email-intro" />
          </label>
          {preview?.sent_at && <p className="text-xs text-emerald-700" data-testid="parent-email-sent-at">Déjà envoyée le {formatDateSafe(preview.sent_at, { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" })}</p>}
          {preview && !preview.smtp_configured && <p className="text-xs text-amber-700" data-testid="parent-email-no-smtp">Configuration SMTP manquante : Mon profil → Messagerie & IA.</p>}
        </div>
        {preview ? <iframe title="Aperçu" srcDoc={preview.html} sandbox="" className="w-full h-[46vh] border rounded-lg bg-[#f1f5f9]" data-testid="parent-email-preview" />
          : <div className="h-[46vh] flex items-center justify-center text-muted-foreground"><Loader2 className="w-5 h-5 animate-spin" /></div>}
        <p className="text-xs text-muted-foreground">PDF « Guidance parentale » joint automatiquement · logos, signature du Cabinet et mention RGPD · les notes réservées au thérapeute ne sont jamais envoyées.</p>
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="parent-email-cancel">Annuler</Button>
          <Button onClick={send} disabled={busy !== "" || !preview?.smtp_configured} className="gap-1.5" data-testid="parent-email-send">
            {busy === "send" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} {busy === "send" ? "Envoi…" : "Envoyer aux parents"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
